import { useState, useEffect } from "react";
import { Clock, MapPin, Navigation } from "lucide-react";
import { gigLocalDate } from "@/utils/dateUtils";
import { tokens } from "@/design-system/tokens";

export const GigCountdown = ({ gig, travelMinutes }) => {
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60_000); // Update every minute
    return () => clearInterval(timer);
  }, []);

  if (!gig || !gig.date) return null;

  // Parse gig datetime
  const localDateStr = gigLocalDate(gig.date);
  const parts = (localDateStr || '').split("-");
  const gigDate = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));

  if (gig.start_time) {
    const [h, m] = gig.start_time.split(":").map(Number);
    gigDate.setHours(h, m, 0, 0);
  } else {
    gigDate.setHours(20, 0, 0, 0); // default 8pm if no time
  }

  const diffMs = gigDate - now;
  if (diffMs < -3 * 60 * 60 * 1000) return null; // Gig ended 3h ago

  const diffMin = Math.floor(diffMs / 60_000);
  const diffHours = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHours / 24);

  // Format countdown text
  let countdownText;
  let subText = "";
  // Compare dates as YYYY-MM-DD strings in the user's local timezone only.
  // No UTC timestamps, no Date math — avoids DST + locale edge cases.
  const todayStr = now.toLocaleDateString('en-CA'); // "YYYY-MM-DD" in local tz
  const gigDayStr = localDateStr;                    // user-local YYYY-MM-DD
  const dayDiff = Math.round(
    (new Date(gigDayStr + 'T00:00:00') - new Date(todayStr + 'T00:00:00')) / 86400000
  );
  const isToday = dayDiff === 0;
  const isTomorrow = dayDiff === 1;
  const timeStr = gig.start_time || "8pm";

  if (diffMs < 0) {
    countdownText = "Happening now";
  } else if (diffMin < 60) {
    countdownText = `${diffMin} min to showtime`;
  } else if (isToday) {
    countdownText = `Tonight at ${timeStr}`;
  } else if (isTomorrow) {
    countdownText = `Tomorrow at ${timeStr}`;
  } else if (diffDays < 7) {
    countdownText = `${diffDays} day${diffDays !== 1 ? "s" : ""}, ${diffHours % 24} hour${(diffHours % 24) !== 1 ? "s" : ""}`;
  } else {
    const weeks = Math.floor(diffDays / 7);
    const remainDays = diffDays % 7;
    countdownText = `${weeks} week${weeks !== 1 ? "s" : ""}${remainDays > 0 ? `, ${remainDays} day${remainDays !== 1 ? "s" : ""}` : ""}`;
  }

  // Leave-by calculation
  if (travelMinutes && diffMs > 0 && diffDays < 2) {
    const leaveBy = new Date(gigDate.getTime() - travelMinutes * 60_000);
    if (leaveBy > now) {
      const leaveH = leaveBy.getHours();
      const leaveM = leaveBy.getMinutes().toString().padStart(2, "0");
      subText = `Leave by ${leaveH}:${leaveM}`;
    } else if (now < gigDate) {
      subText = "Leave now!";
    }
  }

  return (
    <div
      style={{
        backgroundColor: tokens.color.cardBg,
        border: tokens.border.card,
        borderRadius: tokens.radius.lg,
        overflow: "hidden",
        boxShadow: tokens.shadow.card,
        width: "100%"
      }}
      data-testid="gig-countdown"
    >
      {/* Header */}
      <div
        style={{
          padding: `${tokens.space.lg}px ${tokens.space.lg}px`,
          display: "flex",
          alignItems: "center",
          gap: tokens.space.sm,
          borderBottom: tokens.border.hairline
        }}
      >
        <Clock className="h-4 w-4" style={{ color: tokens.color.orange }} />
        <span
          style={{
            ...tokens.type.kicker,
            color: tokens.color.textMuted
          }}
        >
          Countdown
        </span>
      </div>

      <div style={{ padding: `${tokens.space.lg}px ${tokens.space.lg}px`, display: "flex", flexDirection: "column", gap: `${tokens.space.md}px` }}>
        <p
          style={{
            ...tokens.type.statBig,
            color: tokens.color.text,
            margin: 0
          }}
          data-testid="countdown-text"
        >
          {countdownText}
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: `${tokens.space.sm}px` }}>
          <p
            style={{
              ...tokens.type.titleSm,
              color: tokens.color.text,
              margin: 0
            }}
            data-testid="countdown-artist"
          >
            {gig.artist_name}
          </p>
          <p
            style={{
              ...tokens.type.bodySm,
              color: tokens.color.textMuted,
              display: "flex",
              alignItems: "center",
              gap: `${tokens.space.sm}px`,
              margin: 0
            }}
          >
            <MapPin className="h-3.5 w-3.5" style={{ color: tokens.color.textMuted, flexShrink: 0 }} />
            {gig.venue_name}{gig.city ? `, ${gig.city}` : ""}
          </p>
        </div>

        {subText && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: `${tokens.space.sm}px`,
              color: tokens.color.orange,
              fontWeight: 600,
              fontSize: "14px",
              fontFamily: tokens.font.body,
              marginTop: `${tokens.space.sm}px`
            }}
            data-testid="countdown-leave-by"
          >
            <Navigation className="h-3.5 w-3.5" style={{ color: tokens.color.orange, flexShrink: 0 }} />
            {subText}
          </div>
        )}
      </div>
    </div>
  );
};
